/**
 * CanDo Payments — Conversion Journey Controller
 *
 * Purpose: prevent the lead popup (secondary conversion path) from competing
 * with primary conversion paths (contact form, booking flows, calculator
 * lead forms) within a single user session.
 *
 * Storage model:
 *   sessionStorage (cleared when tab closes):
 *     - cdp_leadCaptured       "1" after ANY generate_lead fires
 *     - cdp_activePrimaryForm  form_name when a primary form is touched
 *     - cdp_popupShown         "1" once the popup has been displayed
 *   localStorage (persistent across sessions):
 *     - cdp_popupDismissedAt   unix ms timestamp of last dismissal
 *
 * Popup-only form name. Every other form name is considered a "primary" flow.
 */

import { isAnalyticsGranted } from "@/utils/googleTag";
import { GUIDE_IDS } from "@/data/guidesContent";

const POPUP_FORM_NAME = "lead_popup_health_check"; // LeadPopup schema id

const KEYS = {
  leadCaptured: "cdp_leadCaptured",
  activePrimaryForm: "cdp_activePrimaryForm",
  popupShown: "cdp_popupShown",
  popupDismissedAt: "cdp_popupDismissedAt",
  // Journey context for attribution (last meaningful interaction per type)
  lastProductClick: "cdp_lastProductClick",
  lastToolOpen: "cdp_lastToolOpen",
  lastPartnerClick: "cdp_lastPartnerClick",
  // guideId of the FIRST comparison guide the visitor left via a CanDo link
  // in this tab (comparison-guides-v1). Analytics attribution: only written
  // or read while analytics consent is granted, cleared on withdrawal.
  // sessionStorage → lives for the tab, survives reloads, gone when the tab
  // closes. Never sent to the backend or CRM.
  originGuide: "cdp_originGuide",
};

const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Routes where an embedded higher-intent form already exists.
// The popup must never appear on these paths.
const SUPPRESSED_PATH_PREFIXES = [
  "/contact",
  "/book",
  "/switch-and-save",
  "/overpaying-scorecard",
  "/hidden-fees-checker",
  "/cash-vs-card",
  "/switch-vs-stay",
  "/statement-review",
  "/payment-finder",
  "/quote",
];

function ss() {
  try { return window.sessionStorage; } catch (_err) { return null; /* sessionStorage may be unavailable (Safari private mode) */ }
}
function ls() {
  try { return window.localStorage; } catch (_err) { return null; /* localStorage may be unavailable (Safari private mode) */ }
}

function read(storage, key) {
  if (!storage) return null;
  try { return storage.getItem(key); } catch (_err) { return null; /* getItem may throw under quota/privacy restrictions */ }
}
function remove(storage, key) {
  if (!storage) return;
  try { storage.removeItem(key); } catch (_err) { /* storage may be unavailable — nothing to clear */ }
}
function write(storage, key, value) {
  if (!storage) return;
  try { storage.setItem(key, value); } catch (_err) { /* setItem may throw when quota exceeded or in private mode — safe to swallow, app continues */ }
}

// ─── Public state helpers ────────────────────────────────────

export function isPopup(formName) {
  return formName === POPUP_FORM_NAME;
}

export function markPrimaryFormStarted(formName) {
  if (!formName || isPopup(formName)) return;
  write(ss(), KEYS.activePrimaryForm, formName);
}

export function markLeadCaptured() {
  write(ss(), KEYS.leadCaptured, "1");
}

export function markPopupShown() {
  write(ss(), KEYS.popupShown, "1");
}

export function markPopupDismissed() {
  write(ls(), KEYS.popupDismissedAt, String(Date.now()));
}

// ─── Journey attribution context ─────────────────────────────
// Lightweight, session-scoped breadcrumbs for the last meaningful
// interaction per category. Used by analytics to enrich downstream
// conversion events with traffic_context.
export function markLastProductClick(itemName) {
  if (itemName) write(ss(), KEYS.lastProductClick, itemName);
}
export function markLastToolOpen(itemName) {
  if (itemName) write(ss(), KEYS.lastToolOpen, itemName);
}
export function markLastPartnerClick(itemName) {
  if (itemName) write(ss(), KEYS.lastPartnerClick, itemName);
}
// First eligible guide wins: a later guide click never replaces it.
export function markOriginGuide(guideId) {
  if (!isAnalyticsGranted() || !GUIDE_IDS.includes(guideId)) return;
  if (getOriginGuide()) return;
  write(ss(), KEYS.originGuide, guideId);
}
// Always a controlled value: the stored guideId, or "" (never a stale or
// unrecognised value, and "" whenever analytics consent isn't granted).
export function getOriginGuide() {
  if (!isAnalyticsGranted()) return "";
  const v = read(ss(), KEYS.originGuide);
  return GUIDE_IDS.includes(v) ? v : "";
}
export function clearOriginGuide() {
  remove(ss(), KEYS.originGuide);
}
export function getTrafficContext() {
  return {
    last_product_clicked: read(ss(), KEYS.lastProductClick) || "",
    last_tool_opened: read(ss(), KEYS.lastToolOpen) || "",
    last_partner_clicked: read(ss(), KEYS.lastPartnerClick) || "",
  };
}

// ─── Suppression checks ──────────────────────────────────────

function isOnSuppressedPath() {
  const path = window.location.pathname || "/";
  return SUPPRESSED_PATH_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

function leadAlreadyCaptured() {
  return read(ss(), KEYS.leadCaptured) === "1";
}

function primaryFormActive() {
  return !!read(ss(), KEYS.activePrimaryForm);
}

function popupAlreadyShown() {
  return read(ss(), KEYS.popupShown) === "1";
}

function popupInCooldown() {
  const ts = parseInt(read(ls(), KEYS.popupDismissedAt) || "0", 10);
  if (!ts) return false;
  return (Date.now() - ts) < DISMISS_COOLDOWN_MS;
}

/**
 * Detect GTM Preview / Debug mode and Tag Assistant so we do not
 * pollute debug sessions with secondary-path events.
 */
function isGtmDebugMode() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has("gtm_debug") || params.has("gtm_auth") || params.has("gtm_preview")) return true;
    if (document.referrer && document.referrer.includes("tagassistant.google.com")) return true;
    const gtm = window.google_tag_manager;
    if (gtm && typeof gtm === "object") {
      for (const k of Object.keys(gtm)) {
        const entry = gtm[k];
        if (entry && entry.debug) return true;
      }
    }
  } catch (_err) { /* Defensive: detection must never throw, fall through to "not in debug mode". */ }
  return false;
}

/**
 * Single source of truth: should the lead popup be allowed to show right now?
 * Returns { allowed: boolean, reason?: string }.
 */
export function canShowLeadPopup() {
  if (isGtmDebugMode()) return { allowed: false, reason: "gtm_debug" };
  if (isOnSuppressedPath()) return { allowed: false, reason: "suppressed_path" };
  if (leadAlreadyCaptured()) return { allowed: false, reason: "lead_captured" };
  if (primaryFormActive()) return { allowed: false, reason: "primary_form_active" };
  if (popupAlreadyShown()) return { allowed: false, reason: "already_shown" };
  if (popupInCooldown()) return { allowed: false, reason: "dismiss_cooldown" };
  return { allowed: true };
}

export const JOURNEY_KEYS = KEYS;
export const POPUP_DISMISS_COOLDOWN_MS = DISMISS_COOLDOWN_MS;
